import type { DotProbeConfig } from '@/domain/core/protocol.schema';
import { createRng } from '@/domain/core/rng';
import type { Phase, RenderOp, ResponseCode, StageGeometry, TrialSpec } from '@/domain/core/types';
import { wordAssetId, type WordStimulusSet } from '@/domain/stimuli/types';

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Arm = 'training' | 'sham' | 'assessment' | 'practice';

export interface DotProbeMeta {
  arm: Arm;
  trialType: 'threat-neutral' | 'neutral-neutral';
  pairId: string;
  /** 위협 자극의 위치. neutral-neutral 이면 null */
  threatSide: Side | null;
  probeSide: Side;
  /** congruent = 탐침이 위협 위치. Price 2015 하위분석을 위해 probeSide 도 반드시 같이 남긴다. */
  congruency: 'congruent' | 'incongruent' | 'neutral';
  soaMs: number;
}

interface Slot { threatSide: Side | null; probeSide: Side; pairIdx: number; trialType: DotProbeMeta['trialType'] }

/**
 * 트라이얼 리스트를 생성한다. 완전 결정적 — 같은 (config, stimuli, seed, geometry) 면 같은 결과.
 *
 * 균형화 원칙:
 *  - 위협 위치는 정확히 반반 (무작위 배정이 아니라 카운터밸런스)
 *  - 평가 arm 은 congruent/incongruent 를 정확히 명세 수만큼 (우연성 0.5 를 "확률"이 아니라 "개수"로)
 *  - 훈련 arm 은 우연성 × 위협-중립 시행수를 반올림한 개수만큼 탐침을 중립 쪽에
 *  - 마지막에 전체 셔플. 같은 쌍이 연속되지 않도록 1회 재시도.
 */
export function generateDotProbeTrials(
  config: DotProbeConfig,
  stimuli: WordStimulusSet,
  seed: number,
  geometry: StageGeometry,
  arm: Arm,
  blockId = arm,
): TrialSpec<DotProbeMeta>[] {
  const rng = createRng(seed);
  const [sideA, sideB]: [Side, Side] = config.arrangement === 'vertical' ? ['top', 'bottom'] : ['left', 'right'];

  const slots: Slot[] = [];
  const pairs = stimuli.threatNeutralPairs;
  const neutrals = stimuli.neutralNeutralPairs;
  if (pairs.length === 0) throw new Error('위협-중립 쌍이 없습니다');

  const pushThreatNeutral = (n: number, probeAtNeutralCount: number): void => {
    // 위협 위치 반반, 그 안에서 탐침 위치를 명세 개수만큼 배정
    const order: Slot[] = [];
    for (let i = 0; i < n; i += 1) {
      const threatSide = i % 2 === 0 ? sideA : sideB;
      order.push({ threatSide, probeSide: threatSide, pairIdx: i % pairs.length, trialType: 'threat-neutral' });
    }
    // 처음 probeAtNeutralCount 개를 incongruent 로 뒤집되, 위협 위치 양쪽에 고르게
    const idx = rng.shuffle(order.map((_, i) => i));
    let flippedA = 0, flippedB = 0;
    const perSide = Math.ceil(probeAtNeutralCount / 2);
    for (const i of idx) {
      const s = order[i] as Slot;
      const isA = s.threatSide === sideA;
      if (isA && flippedA < perSide) { s.probeSide = sideB; flippedA += 1; }
      else if (!isA && flippedB < probeAtNeutralCount - perSide) { s.probeSide = sideA; flippedB += 1; }
      if (flippedA + flippedB >= probeAtNeutralCount) break;
    }
    slots.push(...order);
  };

  const pushNeutralNeutral = (n: number): void => {
    for (let i = 0; i < n; i += 1) {
      slots.push({ threatSide: null, probeSide: i % 2 === 0 ? sideA : sideB, pairIdx: i % Math.max(1, neutrals.length), trialType: 'neutral-neutral' });
    }
  };

  switch (arm) {
    case 'training': {
      const tn = config.training.threatNeutral;
      pushThreatNeutral(tn, Math.round(tn * config.training.contingency));
      pushNeutralNeutral(config.training.neutralNeutral);
      break;
    }
    case 'sham': {
      const tn = config.training.threatNeutral;
      pushThreatNeutral(tn, Math.round(tn * config.sham.contingency));
      pushNeutralNeutral(config.training.neutralNeutral);
      break;
    }
    case 'assessment': {
      const { congruent, incongruent, neutralNeutral } = config.assessment;
      pushThreatNeutral(congruent + incongruent, incongruent);
      pushNeutralNeutral(neutralNeutral);
      break;
    }
    case 'practice': {
      pushNeutralNeutral(config.practice.total);
      break;
    }
  }

  let ordered = rng.shuffle(slots);
  if (hasAdjacentSamePair(ordered)) ordered = rng.shuffle(slots);

  const feedback = arm === 'practice' && config.practice.feedback;

  return ordered.map((slot, index) => {
    const pairId = slot.trialType === 'threat-neutral'
      ? (pairs[slot.pairIdx] as WordStimulusSet['threatNeutralPairs'][number]).id
      : (neutrals[slot.pairIdx] as WordStimulusSet['neutralNeutralPairs'][number]).id;
    const [textA, textB] = stimulusTexts(slot, stimuli, sideA);
    const congruency: DotProbeMeta['congruency'] =
      slot.threatSide === null ? 'neutral' : slot.probeSide === slot.threatSide ? 'congruent' : 'incongruent';

    return {
      index,
      blockId,
      isTraining: arm === 'training' || arm === 'sham',
      correctResponse: slot.probeSide as ResponseCode,
      meta: { arm, trialType: slot.trialType, pairId, threatSide: slot.threatSide, probeSide: slot.probeSide, congruency, soaMs: config.cueSoaMs },
      phases: buildPhases(config, geometry, sideA, sideB, textA, textB, slot.probeSide, feedback),
    };
  });
}

function stimulusTexts(slot: Slot, stimuli: WordStimulusSet, sideA: Side): [string, string] {
  if (slot.trialType === 'threat-neutral') {
    const p = stimuli.threatNeutralPairs[slot.pairIdx] as WordStimulusSet['threatNeutralPairs'][number];
    return slot.threatSide === sideA ? [p.threat, p.neutral] : [p.neutral, p.threat];
  }
  const n = stimuli.neutralNeutralPairs[slot.pairIdx] as WordStimulusSet['neutralNeutralPairs'][number];
  // 중립쌍의 좌우/상하는 pairIdx 홀짝으로 교대
  return slot.pairIdx % 2 === 0 ? [n.a, n.b] : [n.b, n.a];
}

function hasAdjacentSamePair(xs: Slot[]): boolean {
  for (let i = 1; i < xs.length; i += 1) {
    const a = xs[i - 1] as Slot, b = xs[i] as Slot;
    if (a.trialType === b.trialType && a.pairIdx === b.pairIdx) return true;
  }
  return false;
}

function pos(side: Side, g: StageGeometry): { x: number; y: number } {
  const half = g.separation / 2;
  switch (side) {
    case 'top':    return { x: g.centerX, y: g.centerY - half };
    case 'bottom': return { x: g.centerX, y: g.centerY + half };
    case 'left':   return { x: g.centerX - half, y: g.centerY };
    case 'right':  return { x: g.centerX + half, y: g.centerY };
  }
}

function buildPhases(
  c: DotProbeConfig, g: StageGeometry, sideA: Side, sideB: Side,
  textA: string, textB: string, probeSide: Side, feedback: boolean,
): Phase[] {
  const pA = pos(sideA, g), pB = pos(sideB, g), pP = pos(probeSide, g);
  const cueOps: RenderOp[] = [
    { op: 'clear' },
    { op: 'bitmap', assetId: wordAssetId(textA), x: pA.x, y: pA.y },
    { op: 'bitmap', assetId: wordAssetId(textB), x: pB.x, y: pB.y },
  ];
  const phases: Phase[] = [
    { id: 'fixation', durationMs: c.fixationMs, acceptsInput: false,
      ops: [{ op: 'clear' }, { op: 'fixation', x: g.centerX, y: g.centerY, size: g.fixationSize }] },
    { id: 'cue', durationMs: c.cueSoaMs, acceptsInput: false, ops: cueOps },
    { id: 'probe', durationMs: 'until-response', timeoutMs: c.probeTimeoutMs, acceptsInput: true, rtAnchor: true,
      ops: [{ op: 'clear' }, { op: 'probe', kind: 'two-dot', x: pP.x, y: pP.y, size: g.probeSize }] },
  ];
  if (feedback) phases.push({ id: 'feedback', durationMs: c.feedbackMs, acceptsInput: false, ops: [{ op: 'clear' }, { op: 'feedback' }] });
  phases.push({ id: 'iti', durationMs: c.itiMs, acceptsInput: false, ops: [{ op: 'clear' }] });
  return phases;
}
